import { readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";
import { usePlatformAuthStore, type PlatformUser } from "@/stores/platformAuthStore";

/**
 * MT-1 AUTH-009 (FR-6.2, FR-6.9): the platform console's sign-in calls. Every one goes through
 * `platformFetch` (CSRF on every mutation, never the school token). A refusal throws the API's
 * error (`readApiErrorResponse`) for `apiErrorText`.
 */
async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await platformFetch(path, { method: "POST", body: JSON.stringify(body) });
  if (!res.ok) throw await readApiErrorResponse(res);
  const json = (await res.json()) as { data: T };
  return json.data;
}

export type PlatformPendingChallenge = {
  challengeToken: string;
  maskedEmail?: string;
  expiresAt?: string;
};

/** The password step: the code is mailed through the platform account. */
export function platformLogin(email: string, password: string) {
  return post<PlatformPendingChallenge>("/platform/auth/login", { email, password });
}

/** The code step: stores the platform session (the refresh cookie is set by the response). */
export async function platformVerify(challengeToken: string, code: string): Promise<PlatformUser> {
  const data = await post<{ accessToken: string; user: PlatformUser }>("/platform/auth/verify", {
    challengeToken,
    code,
  });
  usePlatformAuthStore.getState().setSession(data.accessToken, data.user);
  return data.user;
}

export function platformResendCode(challengeToken: string) {
  return post<{ expiresAt: string }>("/platform/auth/resend", { challengeToken });
}

export function platformSetPassword(token: string, password: string) {
  return post<{ ok: true }>("/platform/auth/set-password", { token, password });
}

/** Answers the same for every address (the link, if any, is mailed later). */
export function platformForgotPassword(email: string) {
  return post<unknown>("/platform/auth/forgot-password", { email });
}
